import React, { createContext, useContext, useRef, useCallback } from 'react';
import * as THREE from 'three';

interface PlayerContextProps {
	teleportPlayer: (x: number, y: number, z: number) => void;
	registerPlayerApi: (api: any) => void;
	getPlayerPosition: () => [number, number, number] | null;
}

const PlayerContext = createContext<PlayerContextProps | null>(null);

export const usePlayerContext = () => {
	const ctx = useContext(PlayerContext);
	if (!ctx) {
		throw new Error('usePlayerContext must be used within a PlayerProvider');
	}
	return ctx;
};

export const PlayerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const playerApiRef = useRef<any>(null);
	const playerPositionRef = useRef<[number, number, number]>([0, 0, 0]);

	const registerPlayerApi = useCallback((api: any) => {
		playerApiRef.current = api;
		// Subscribe to position changes
		api.position.subscribe((pos: [number, number, number]) => {
			playerPositionRef.current = pos;
		});
	}, []);

	const teleportPlayer = useCallback((x: number, y: number, z: number) => {
		if (playerApiRef.current) {
			playerApiRef.current.position.set(x, y, z);
			playerApiRef.current.velocity.set(0, 0, 0); // Reset velocity to prevent weird physics after teleport
		}
	}, []);

	const getPlayerPosition = useCallback(() => {
		return playerPositionRef.current;
	}, []);

	return (
		<PlayerContext.Provider value={{ teleportPlayer, registerPlayerApi, getPlayerPosition }}>
			{children}
		</PlayerContext.Provider>
	);
};
